<template>
    <div class="YooKassaWidget">
      <div class="YooKassaWidget__grid">
        <div class="grid gap-3 items-center justify-center text-center">
          <span class="font-semibold text-3xl">Оплата заказа</span>
          <span v-if="orderId" class="text-xl text-gray-500">Заказ №{{ orderId }}</span>
          <span v-if="amount" class="text-base font-bold text-orange-600">К оплате: {{ amount }} ₽</span>
        </div>

        <div v-if="!confirmationToken" class="text-center text-gray-500 py-8">
          Не удалось получить токен для оплаты. Попробуйте ещё раз.
        </div>
        <div v-else id="yookassa-widget" ref="widgetContainer" :key="widgetKey" class="min-h-[440px]" style="color-scheme: light"></div>
        <div v-if="isProcessing" class="text-center text-orange-500 mt-2">Ожидаем подтверждения оплаты...</div>
      </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick, computed } from 'vue';
import { consola } from 'consola';
import apiProfileInstance from '~/entities/user';
import { usePaymentStore, usePaymentReconciliation } from '@features/payment';
import { useModalStore , modalStateEnum } from '@shared/model';

import { useDarkMode } from '@shared/lib';
import { useCartStore } from '@entities/order';

const logger = consola.withTag('YooKassaWidget');

const paymentStore = usePaymentStore();
const storeModal = useModalStore();
const { isDark } = useDarkMode();
const cartStore = useCartStore();
const { pollUntilPaid } = usePaymentReconciliation();

const confirmationToken = paymentStore.confirmationToken;
const orderId = paymentStore.orderId;
const amount = computed(() => paymentStore.amount || null);
const isProcessing = ref(false);
const widgetContainer = ref<HTMLElement | null>(null);
const widgetKey = ref(0); // Для полного пересоздания виджета

let widgetInstance: any = null;

function handleClose() {
  // TODO возможно тут происходит очистка
  // paymentStore.resetPayment();
  storeModal.close(modalStateEnum.YooKassaWidget);
  storeModal.open(modalStateEnum.ModalThankYou);
}

// ---- WIDGET ----
async function renderWidget() {
  logger.log('🔧 YooKassaWidget.renderWidget called with:', {
    confirmationToken,
    hasContainer: !!widgetContainer.value,
    orderId,
    amount: amount.value,
  });

  if (!confirmationToken || !widgetContainer.value) {
    logger.error('❌ YooKassaWidget.renderWidget: missing required data', {
      confirmationToken: !!confirmationToken,
      hasContainer: !!widgetContainer.value,
    });
    return;
  }

  // Динамически подгружаем скрипт виджета, если ещё не подключён
  if (!(window as any).YooMoneyCheckoutWidget) {
    const script = document.createElement('script');
    script.src = 'https://yookassa.ru/checkout-widget/v1/checkout-widget.js';
    script.async = true;
    script.addEventListener('load', () => renderWidget());
    document.body.append(script);
    return;
  }

  widgetContainer.value.innerHTML = '';
  isProcessing.value = false;

  widgetInstance = new (window as any).YooMoneyCheckoutWidget({
    confirmation_token: confirmationToken,
    theme: isDark.value ? 'dark' : 'light',
    customization: {
      colors: isDark.value
        ? {
          background: '#0D182F',
          control_primary: '#00BF96',
          control_primary_content: '#FFFFFF',
          control_secondary: '#366093',
          border: '#244166',
          text: '#DBDCE0',
        }
        : {
          background: '#FFFFFF',
          control_primary: '#00BF96',
          control_primary_content: '#18181b',
          control_secondary: '#EDF2F7',
          border: '#E2E8F0',
          text: '#232323',
        },
    },
    error_callback: (error: any) => {
      paymentStore.setStatus('failed');
      paymentStore.setError(error?.message || 'Ошибка оплаты');
      storeModal.open(modalStateEnum.Error);
      widgetInstance.destroy?.();
      handleClose();
    },
  });

  // Слушаем событие завершения оплаты (успешно/отказ/отмена)
  widgetInstance.on('complete', async () => {
    // Оплата уже прошла — падение очистки не должно ронять обработчик.
    // Корзину досверит reconcilePaidOrder по статусу заказа.
    cartStore.clearCart().catch(() => {})
    paymentStore.setStatus('succeeded');
    const { status, payment_status } = await apiProfileInstance.getOrder(paymentStore.orderId!);

    // Обновляем оба поля через твой метод
    paymentStore.updateOrderField('status', status);
    paymentStore.updateOrderField('payment_status', payment_status);
    storeModal.open(modalStateEnum.ModalThankYou);
    widgetInstance.destroy?.();
    handleClose();
  });


  widgetInstance.render('yookassa-widget');
}
// ---- END WIDGET ----

// Поллинг статуса заказа: закрывает модалку при оплате независимо от события виджета
// `complete` — например, когда платят по fallback-ссылке в другой вкладке.
let stopPoll: (() => void) | null = null;

onBeforeUnmount(() => {
  stopPoll?.();
  stopPoll = null;
});

onMounted(async () => {
  logger.log('🚀 YooKassaWidget mounted with:', {
    confirmationToken,
    orderId,
    amount: amount.value,
    hasContainer: !!widgetContainer.value,
  });

  stopPoll = pollUntilPaid(paymentStore.orderId, () => handleClose());

  nextTick(() => {
    renderWidget();
  });
});

// Перерендерить виджет при смене токена
watch(
  () => paymentStore.confirmationToken,
  (newVal, oldVal) => {
    logger.log('🔄 YooKassaWidget token changed:', {
      oldToken: oldVal,
      newToken: newVal,
    });

    if (newVal) {
      widgetKey.value += 1;
      nextTick(() => {
        renderWidget();
      });
    }
  },
);

// Перерендерить виджет при смене темы
watch(isDark, () => {
  widgetKey.value += 1;
  nextTick(() => {
    renderWidget();
  });
});
</script>


<style scoped lang="scss">
.YooKassaWidget {
  @apply p-2.5 bg-white dark:bg-gray-900;

  &__grid {
    @apply grid gap-5 content-center md:gap-10 bg-sky-300 dark:bg-zinc-900 rounded-2xl  pb-4 px-5 md:py-8 md:pr-20 md:pl-10 max-md:min-h-[calc(100vh-20px)] max-md:items-center;
  }
}
</style>
